import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { HiChevronDown } from "react-icons/hi";
import { useIntl } from "react-intl";
import { motion } from "framer-motion";
import { useLanguage } from "../../context";
import { setLocalStorage } from "../../helpers/localStorage";

/**
 * Sélecteur de langue pour le portail admin.
 * Change la langue via le contexte sans redirection d'URL
 * (les routes /admin n'ont pas de préfixe linguistique).
 */
const AdminLanguage = () => {
  const intl = useIntl();
  const { language, changeLanguage: setLanguage } = useLanguage();
  const [isChanging, setIsChanging] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);

  const langs = [
    { key: "en", value: intl.formatMessage({ id: "en" }) },
    { key: "fr", value: intl.formatMessage({ id: "fr" }) },
  ];

  const updateDropdownPosition = () => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const dropdownWidth = 160;
    const left = Math.min(rect.left, Math.max(8, window.innerWidth - dropdownWidth - 8));
    setDropdownPosition({ top: rect.bottom + 8, left });
  };

  const toggleDropdown = () => {
    setIsDropdownOpen((open) => {
      if (!open) updateDropdownPosition();
      return !open;
    });
  };

  useEffect(() => {
    if (!isDropdownOpen) return;
    const handleClickOutside = (event) => {
      if (buttonRef.current && !buttonRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDropdownOpen]);

  useEffect(() => {
    if (!isDropdownOpen) return;
    const handlePositionChange = () => updateDropdownPosition();
    window.addEventListener("scroll", handlePositionChange, true);
    window.addEventListener("resize", handlePositionChange);
    return () => {
      window.removeEventListener("scroll", handlePositionChange, true);
      window.removeEventListener("resize", handlePositionChange);
    };
  }, [isDropdownOpen]);

  const changeLanguage = async (value) => {
    if (language === value || isChanging) return;
    setIsChanging(true);
    try {
      setLocalStorage("@language", value);
      await setLanguage(value);
    } finally {
      setIsChanging(false);
      setIsDropdownOpen(false);
    }
  };

  return (
    <div className="relative">
      <motion.button
        ref={buttonRef}
        type="button"
        onClick={toggleDropdown}
        disabled={isChanging}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-sm text-slate-500 hover:text-emerald-600 hover:bg-slate-100 transition-colors focus:outline-none"
        aria-label="Language"
      >
        {isChanging ? (
          <motion.div
            className="w-3.5 h-3.5 border border-slate-400 border-t-transparent rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
        ) : (
          <>
            <span className="text-xs font-semibold uppercase tracking-wide">
              {intl.formatMessage({ id: language })}
            </span>
            <motion.div
              animate={{ rotate: isDropdownOpen ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <HiChevronDown className="text-slate-400" size={14} />
            </motion.div>
          </>
        )}
      </motion.button>

      {isDropdownOpen &&
        createPortal(
          <ul
            className="fixed z-[9999999] min-w-[160px] list-none overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl text-sm"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
            }}
          >
            {langs.map((lang) => (
              <li key={lang.key}>
                <button
                  type="button"
                  disabled={isChanging}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={() => changeLanguage(lang.key)}
                  className={`w-full flex items-center justify-between px-3 py-2 text-left transition-colors ${
                    language === lang.key
                      ? "bg-emerald-600 text-white"
                      : "text-gray-800 hover:bg-emerald-50 hover:text-emerald-800"
                  }`}
                >
                  <span>{lang.value}</span>
                  {language === lang.key && (
                    <svg className="w-4 h-4 ml-2" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </button>
              </li>
            ))}
          </ul>,
          document.body
        )}
    </div>
  );
};

export default AdminLanguage;
